import { fg } from '@atlaskit/platform-feature-flags';

import type {
	CreateSelectFilter,
	SelectFilterDepParams,
	SelectFilterDeps,
	SelectFilterOption,
	SelectFilterOptionChange,
	SelectFilterState,
	SelectFilterType,
} from '../../../common/constants/filters/select-filter/types';
import { type ProductKeys } from '../../../common/constants/product-keys';

import { getOptions } from './get-options';
import { getProductForFilterOption } from './get-product-for-filter-option';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getOptions } from './get-options';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { generateQueryParamsGetState } from './generate-query-params-get-state';

/**
 * Composes a select filter from its initial definition, current state, and config.
 *
 * Wires up state accessors, option loading, query param generation, and selection
 * management for the filter pipeline without requiring React — the hook wrapper
 * (useSelectFilter) is separate.
 */
export const SelectFilter: SelectFilterType = ({ initial, state, config }) => {
	const filterState = Object.freeze({
		...state,
	});

	return {
		id: initial.id,
		type: filterState.type,
		getState: getState({ initial, state: filterState, config }),
		queryParams: {
			key: initial.queryParams.key,
			get: getQueryParams({ initial, state: filterState, config }),
			set: setQueryParams({ initial, state: filterState, config }),
		},
		loadInitialOptions: loadInitialOptions({ initial, state: filterState, config }),
		setOption: setOption({ initial, state: filterState, config }),
		clearOptions: clearOptions({ initial, state: filterState, config }),
		setLookupInput: setLookupInput({ initial, state: filterState, config }),
		getOptions: getOptions({ initial, state: filterState, config }),
		getValue: getValue({ initial, state: filterState, config }),
		clear: deselectOptions({ initial, state: filterState, config }),
	};
};

/** Returns the current state of a select filter (selected values, options, lookup input). */
export const getState: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => () => {
	options: SelectFilterOption[];
	selectedOptions: SelectFilterOption[];
	hasSelection: boolean;
	value: string[];
	id: ID;
	type: 'single-select' | 'multi-select';
	didMount: boolean;
	didMountPerProducts: Record<string, boolean>;
	alwaysVisible: boolean;
	universal: boolean;
	queryParams: {
		options: SelectFilterOption[];
		isLoading: boolean;
	};
	initialOptions: {
		options: SelectFilterOption[];
		isLoading: boolean;
	};
	lookup: {
		options: SelectFilterOption[];
		lookupResultOptions: SelectFilterOption[];
		input: string;
		isLoading: boolean;
		isActive: boolean;
		isDefined: boolean;
	};
	products: ProductKeys[];
	customValue?: Record<string, string>;
	customValueFields?: string[];
} = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return () => {
		const filter = SelectFilter({ initial, state, config });
		const allOptions = filter.getOptions({ isLookupActive: false });
		const newOptions = filter.getOptions({
			isLookupActive: state.lookup.isActive,
		});
		const selectedOptions = allOptions.filter((option) => option.isSelected);

		const newState = {
			...state,
			options: newOptions,
			selectedOptions,
			hasSelection: selectedOptions.length > 0,
			value: filter.getValue(),
		} satisfies SelectFilterState<ID>;
		return newState;
	};
};

// TODO: >>>> Write more tests here on edge cases
/** Sets the query parameters for a select filter from URL or external state. */
export const setQueryParams: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => (
	queryParams: Record<string, string[] | undefined>,
	onStateChange?: (state: SelectFilterState<string>) => void,
) => Promise<
	import('../../../common/constants/filters/select-filter/types').SelectFilterMonad<ID>
> = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return async (
		queryParams: Record<string, string[] | undefined>,
		onStateChange?: (state: SelectFilterState<string>) => void,
	) => {
		const queryParamValues = queryParams[initial.queryParams.key];
		const filter = SelectFilter({ initial, state, config });

		if (!queryParamValues) {
			const clearedOptions = filter.clearOptions();
			onStateChange?.(clearedOptions.getState());
			return clearedOptions;
		}

		const allCurrentOptions = filter.getOptions({ isLookupActive: false });
		const allQueryParamValues = allCurrentOptions.map((option) => option.queryParamValue);
		const queryParamsToHydrate = queryParamValues.filter(
			(value) => !allQueryParamValues.includes(value),
		);

		if (queryParamsToHydrate.length === 0) {
			const newOptions = allCurrentOptions.map(
				(option) =>
					({
						isSelected: queryParamValues.includes(option.queryParamValue),
						value: option.value,
					}) satisfies SelectFilterOptionChange,
			);

			const filterWithNewOptions = filter.setOption(newOptions);
			onStateChange?.(filterWithNewOptions.getState());
			return filterWithNewOptions;
		}

		// At this point we need to hydrate the filters with new options from
		// the query params
		const newQueryParamOptions = [...state.queryParams.options];
		const fetcher = initial.queryParams.fetcher;
		if (fetcher) {
			onStateChange?.({
				...state,
				queryParams: {
					...state.queryParams,
					isLoading: true,
				},
			});

			const fetchedOptions = await fetcher(queryParamsToHydrate, config);
			fetchedOptions.forEach((option) => {
				const mappedOption = {
					isSelected: true,
					trackingKey: option.trackingKey,
					value: option.value,
					queryParamValue: option.queryParamValue,
					isSuggested: false,
					isDefault: option.isDefault ?? false,
					avatarUrl: option.avatarUrl,
					label: option.label,
					metadata: option.metadata,
					product: getProductForFilterOption({ config, isUniversal: initial.universal }),
				} satisfies SelectFilterOption;
				newQueryParamOptions.push(mappedOption);
			});
		} else {
			// The edge case here is where we have an unsupported query param
			// and we don't have a fetcher to hydrate the unsupported query param
			// TODO: >>>> Not sure what to do here
		}

		const selectedOptionsFromQueryParams = newQueryParamOptions.map((option) => ({
			...option,
			isSelected: queryParamValues.includes(option.queryParamValue),
		}));

		const selectedInitialOptionsFromQueryParams = [...state.initialOptions.options].map(
			(option) => ({
				...option,
				isSelected: queryParamValues.includes(option.queryParamValue),
			}),
		);

		const hydratedQueryParams = SelectFilter({
			initial,
			state: {
				...state,
				queryParams: {
					...state.queryParams,
					options: selectedOptionsFromQueryParams,
					isLoading: false,
				},
				initialOptions: {
					...state.initialOptions,
					options: selectedInitialOptionsFromQueryParams,
				},
			},
			config,
		});

		onStateChange?.(hydratedQueryParams.getState());
		return hydratedQueryParams;
	};
};

/** Reads query parameter values from a select filter's current state. */
export const getQueryParams: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => () => {
	[x: string]: string[];
} = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return () => {
		const queryParamValue = SelectFilter({ initial, state, config })
			.getOptions({ isLookupActive: false })
			.filter((option) => option.isSelected)
			.map((option) => option.queryParamValue);

		return { [initial.queryParams.key]: queryParamValue };
	};
};

/** Fetches and sets the initial options for a select filter (e.g. from an API). */
export const loadInitialOptions: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => (
	onStateChange?: (state: SelectFilterState<ID>) => void,
) => Promise<
	import('../../../common/constants/filters/select-filter/types').SelectFilterMonad<ID>
> = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return async (onStateChange?: (state: SelectFilterState<ID>) => void) => {
		const didMount = fg('product_based_filter_options')
			? state.didMountPerProducts[
					getProductForFilterOption({ config, isUniversal: initial.universal })
				]
			: state.didMount;
		if (didMount) {
			// Already loaded — return the current monad without dispatching a state change.
			// Calling onStateChange here would cause a no-op store write (new object identity
			// for the same data), which makes downstream selectors that return derived references
			// (e.g. useFilters() consumers that compute new arrays/Sets) re-render. When the
			// caller is a useEffect that depends on derived values, this can trigger an
			// infinite render loop. The store should only change when something actually changed.
			return SelectFilter({
				initial,
				state,
				config,
			});
		}

		const newOptions = [...state.initialOptions.options];
		const fetcher = initial.initialOptions?.fetcher;

		if (fetcher) {
			const fetchingInitialOptionsState = {
				...state,
				initialOptions: {
					...state.initialOptions,
					isLoading: true,
				},
			} satisfies SelectFilterState<ID>;

			onStateChange?.(fetchingInitialOptionsState);

			try {
				const fetchedOptions = await fetcher(config);
				fetchedOptions.forEach((option) => {
					const mappedOption = {
						isSelected: false,
						trackingKey: option.trackingKey,
						value: option.value,
						queryParamValue: option.queryParamValue,
						isSuggested: true,
						isDefault: option.isDefault ?? false,
						avatarUrl: option.avatarUrl,
						label: option.label,
						metadata: option.metadata,
						product: getProductForFilterOption({ config, isUniversal: initial.universal }),
					} satisfies SelectFilterOption;
					newOptions.push(mappedOption);
				});
			} catch {
				const fetchFailedState = {
					...state,
					initialOptions: {
						...state.initialOptions,
						isLoading: false,
					},
				} satisfies SelectFilterState<ID>;
				onStateChange?.(fetchFailedState);
			}
		}

		const newInitialOptionsState = {
			...state,
			didMount: true,
			didMountPerProducts: fg('product_based_filter_options')
				? {
						...state.didMountPerProducts,
						[getProductForFilterOption({ config, isUniversal: initial.universal })]: true,
					}
				: state.didMountPerProducts,
			initialOptions: {
				...state.initialOptions,
				options: newOptions,
				isLoading: false,
			},
		} satisfies SelectFilterState<ID>;

		onStateChange?.(newInitialOptionsState);

		return SelectFilter({
			initial,
			state: newInitialOptionsState,
			config,
		});
	};
};

/** Selects or deselects an option in a select filter. */
export const setOption: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => (
	option: SelectFilterOptionChange | SelectFilterOptionChange[],
) => import('../../../common/constants/filters/select-filter/types').SelectFilterMonad<ID> = <
	ID extends string,
>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return (option: SelectFilterOptionChange | SelectFilterOptionChange[]) => {
		const optionsToChange = Array.isArray(option) ? option : [option];
		const optionsToChangeRecord = Object.fromEntries(
			optionsToChange.map((option) => [option.value, option]),
		) as Record<string, SelectFilterOptionChange | undefined>;

		const allExistingValues = new Set([
			...state.queryParams.options.map((o) => o.value),
			...state.initialOptions.options.map((o) => o.value),
			...state.lookup.options.map((o) => o.value),
			...state.lookup.lookupResultOptions.map((o) => o.value),
		]);

		// Options being selected that don't exist in any list yet (e.g. dynamic async-loaded options
		// selected before the store has been populated) are added directly to queryParams.options so
		// they participate in URL param generation immediately.
		const newQueryParamOptions = optionsToChange
			.filter((o) => o.isSelected && !allExistingValues.has(o.value))
			.map(
				(o) =>
					({
						value: o.value,
						queryParamValue: o.value,
						trackingKey: o.value,
						label: o.value,
						isSelected: true,
						isSuggested: false,
						isDefault: false,
						product: getProductForFilterOption({ config, isUniversal: initial.universal }),
					}) satisfies SelectFilterOption,
			);

		const queryParamOptions = [
			...state.queryParams.options.map((option) => {
				if (
					!optionsToChange.find(
						(x) =>
							x.value === option.value &&
							(fg('product_based_filter_options')
								? getProductForFilterOption({ config, isUniversal: initial.universal }) ===
									option.product
								: true),
					)
				) {
					return option;
				}
				return {
					...option,
					isSelected: optionsToChangeRecord[option.value]?.isSelected ?? false,
				};
			}),
			...newQueryParamOptions,
		];

		const initialOptions = state.initialOptions.options.map((option) => {
			if (
				!optionsToChange.find(
					(x) =>
						x.value === option.value &&
						(fg('product_based_filter_options')
							? getProductForFilterOption({ config, isUniversal: initial.universal }) ===
								option.product
							: true),
				)
			) {
				return option;
			}

			return {
				...option,
				isSelected: optionsToChangeRecord[option.value]?.isSelected ?? false,
			};
		});

		const duplicateOptions = new Set();

		const lookupOptions = [
			...state.lookup.options,
			// 'interacted' lookup options are options that a user made a lookup for and has either
			// selected or deselected an option from a lookup query
			// The 'interacted' options are persisted in  state.lookup.options
			...state.lookup.lookupResultOptions.flatMap((option) => {
				if (!optionsToChange.find((x) => x.value === option.value)) {
					return [];
				}

				return [option];
			}),
		]
			.filter(
				(option) => !duplicateOptions.has(option.value) && duplicateOptions.add(option.value), // remove duplicates
			)
			.map((option) => ({
				...option,
				isSuggested: true,
				isSelected: optionsToChangeRecord[option.value]?.isSelected ?? option.isSelected,
			}));

		return SelectFilter({
			initial: initial satisfies CreateSelectFilter<ID>,
			state: {
				...state,
				initialOptions: {
					...state.initialOptions,
					options: initialOptions,
				},
				queryParams: {
					...state.queryParams,
					options: queryParamOptions,
				},
				lookup: {
					...state.lookup,
					options: lookupOptions,
				},
			},
			config,
		});
	};
};

/** Clears all selected options from a select filter. */
export const clearOptions: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => () => import('../../../common/constants/filters/select-filter/types').SelectFilterMonad<ID> = <
	ID extends string,
>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return () => {
		const queryParamOptions = state.queryParams.options.map((option) => ({
			...option,
			isSelected: false,
		}));

		const initialOptions = state.initialOptions.options.map((option) => ({
			...option,
			isSelected: false,
		}));

		const lookupOptions = state.lookup.options.map((option) => ({
			...option,
			isSelected: false,
		}));

		return SelectFilter({
			initial,
			state: {
				...state,
				initialOptions: {
					...state.initialOptions,
					options: initialOptions,
				},
				queryParams: {
					...state.queryParams,
					options: queryParamOptions,
				},
				lookup: {
					...state.lookup,
					options: lookupOptions,
				},
			},
			config,
		});
	};
};

/** Updates the lookup/search input text for a select filter and fetches matching options. */
export const setLookupInput: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => (
	query: string,
	onStateChange?: (state: SelectFilterState<ID>) => void,
) => Promise<
	import('../../../common/constants/filters/select-filter/types').SelectFilterMonad<ID>
> = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return async (query: string, onStateChange?: (state: SelectFilterState<ID>) => void) => {
		if (!initial.lookup || query.length === 0 || state.lookup.input === query) {
			const noQueryState = {
				...state,
				lookup: {
					...state.lookup,
					input: query,
					isLoading: false,
					isActive: false,
				},
			} satisfies SelectFilterState<ID>;
			onStateChange?.(noQueryState);

			return SelectFilter({
				initial,
				state: noQueryState,
				config,
			});
		}

		const fetchingLookupOptionsState = {
			...state,
			lookup: {
				...state.lookup,
				input: query,
				isLoading: true,
				isActive: true,
			},
		} satisfies SelectFilterState<ID>;

		onStateChange?.(fetchingLookupOptionsState);

		try {
			const fetchedLookupOptions = await initial.lookup.fetcher(query, config);
			const newLookupOptions = fetchedLookupOptions.map((option) => {
				const mappedOption = {
					isSelected: false,
					trackingKey: option.trackingKey,
					value: option.value,
					queryParamValue: option.queryParamValue,
					isSuggested: false,
					isDefault: option.isDefault ?? false,
					avatarUrl: option.avatarUrl,
					label: option.label,
					metadata: option.metadata,
					product: getProductForFilterOption({ config, isUniversal: initial.universal }),
				} satisfies SelectFilterOption;
				return mappedOption;
			});

			const newLookupOptionsState = {
				...state,
				lookup: {
					...state.lookup,
					input: query,
					lookupResultOptions: newLookupOptions,
					isLoading: false,
					isActive: true,
				},
			} satisfies SelectFilterState<ID>;

			onStateChange?.(newLookupOptionsState);

			return SelectFilter({
				initial,
				state: newLookupOptionsState,
				config,
			});
		} catch {
			const failedLookupOptionsState = {
				...state,
				lookup: {
					...state.lookup,
					input: query,
					isLoading: false,
					isActive: true,
				},
			} satisfies SelectFilterState<ID>;

			onStateChange?.(failedLookupOptionsState);

			return SelectFilter({
				initial,
				state: failedLookupOptionsState,
				config,
			});
		}
	};
};

/** Returns the currently selected values for a select filter. */
export const getValue: ({ initial, state, config }: SelectFilterDepParams) => () => string[] = ({
	initial,
	state,
	config,
}: SelectFilterDepParams) => {
	return (): string[] => {
		const value = SelectFilter({ initial, state, config })
			.getOptions({ isLookupActive: false })
			.filter((option) => option.isSelected)
			.map((option) => option.value);
		return value;
	};
};

/** Deselects specific options from a select filter by value. */
export const deselectOptions: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => () => import('../../../common/constants/filters/select-filter/types').SelectFilterMonad<ID> = <
	ID extends string,
>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return () => {
		const filter = SelectFilter({ initial, state, config });

		const allOptions = [
			...filter.getOptions({
				isLookupActive: true,
			}),
			...filter.getOptions({
				isLookupActive: false,
			}),
		].map(
			(option) =>
				({
					value: option.value,
					isSelected: false,
				}) satisfies SelectFilterOptionChange,
		);

		return filter.setOption(allOptions);
	};
};
